import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FileText, ShieldAlert, Save, RotateCcw, Loader2, CheckCircle2, ExternalLink, Info } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const PAGES = [
  { slug: "tentang-kami", label: "Tentang Kami" },
  { slug: "kebijakan-privasi", label: "Kebijakan Privasi" },
  { slug: "syarat-ketentuan", label: "Syarat & Ketentuan" },
];

export const PengaturanLegalPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [active, setActive] = useState("tentang-kami");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notif, setNotif] = useState(null);

  const notify = (type, msg) => { setNotif({ type, msg }); setTimeout(() => setNotif(null), 5000); };

  const load = useCallback(async (slug) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/legal/${slug}`);
      setTitle(res.data.title || "");
      setContent(res.data.content || "");
    } catch (e) { notify("error", "Gagal memuat konten"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isAdmin) load(active); }, [active, isAdmin, load]);

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto mt-10" data-testid="pengaturan-legal-denied">
        <div className="p-6 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-center">
          <ShieldAlert className="w-10 h-10 text-rose-400 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-white">Akses Ditolak</h2>
          <p className="text-sm text-slate-400 mt-1">Editor halaman legal hanya untuk <b className="text-rose-300">Owner / Super Admin</b>.</p>
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/legal/${active}`, { title, content }, { withCredentials: true });
      notify("success", "Halaman berhasil disimpan & langsung tampil di halaman publik.");
    } catch (e) { notify("error", e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm("Kembalikan konten halaman ini ke teks default? Perubahan Anda akan hilang.")) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/legal/${active}/reset`, {}, { withCredentials: true });
      setTitle(res.data.title || "");
      setContent(res.data.content || "");
      notify("success", "Konten dikembalikan ke teks default.");
    } catch (e) { notify("error", "Gagal mereset konten"); }
  };

  return (
    <div className="space-y-5 max-w-4xl mx-auto" data-testid="pengaturan-legal-page">
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-cyan-500/20 text-cyan-400 flex items-center justify-center">
          <FileText className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">Editor Halaman Legal</h1>
          <p className="text-xs text-slate-400">Edit Tentang Kami, Kebijakan Privasi &amp; Syarat &amp; Ketentuan — untuk verifikasi payment gateway</p>
        </div>
        <a href={`/${active}`} target="_blank" rel="noopener noreferrer" data-testid="legal-preview-link" className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl flex items-center gap-1.5">
          <ExternalLink className="w-3.5 h-3.5" /> Lihat Halaman
        </a>
      </div>

      {notif && (
        <div data-testid="legal-notif" className={`p-3 rounded-xl text-xs flex items-center gap-2 border ${notif.type === "success" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-rose-500/10 border-rose-500/30 text-rose-300"}`}>
          <CheckCircle2 className="w-4 h-4" /><span>{notif.msg}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {PAGES.map((pg) => (
          <button key={pg.slug} onClick={() => setActive(pg.slug)} data-testid={`legal-tab-${pg.slug}`}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors ${active === pg.slug ? "bg-cyan-600 text-white" : "bg-slate-900 border border-slate-800 text-slate-300 hover:border-cyan-500/40"}`}>
            {pg.label}
          </button>
        ))}
      </div>

      <div className="p-3 rounded-xl bg-cyan-500/5 border border-cyan-500/20 text-[11px] text-slate-400 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 text-cyan-400 mt-0.5 shrink-0" />
        <span>Format sederhana: <code className="text-cyan-300">## Judul</code> untuk sub-judul, <code className="text-cyan-300">- </code> untuk poin, <code className="text-cyan-300">**tebal**</code> untuk teks tebal. Placeholder otomatis terisi: <code className="text-emerald-300">{"{{nama}}"}</code>, <code className="text-emerald-300">{"{{badan_usaha}}"}</code>, <code className="text-emerald-300">{"{{alamat}}"}</code>, <code className="text-emerald-300">{"{{email}}"}</code>, <code className="text-emerald-300">{"{{whatsapp}}"}</code>, <code className="text-emerald-300">{"{{telepon}}"}</code>, <code className="text-emerald-300">{"{{website}}"}</code>.</span>
      </div>

      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400 text-sm gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Memuat…</div>
        ) : (
          <>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Judul Halaman</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="legal-title-input"
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-500" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Isi Konten</label>
              <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={20} data-testid="legal-content-textarea"
                className="w-full px-3 py-3 bg-slate-950 border border-slate-700 rounded-xl text-xs text-slate-100 font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-cyan-500 resize-y" />
            </div>
            <div className="flex justify-between items-center pt-1 border-t border-slate-800">
              <button onClick={handleReset} data-testid="legal-reset-btn" className="px-3 py-2 text-slate-400 hover:text-rose-300 text-xs font-semibold flex items-center gap-1.5">
                <RotateCcw className="w-3.5 h-3.5" /> Kembalikan ke Default
              </button>
              <button onClick={handleSave} disabled={saving} data-testid="legal-save-btn"
                className="px-5 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl shadow-lg shadow-cyan-600/20 flex items-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? "Menyimpan…" : "Simpan Perubahan"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PengaturanLegalPage;
